import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Input,
  Select,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { Button, CustomDateTimeInput, DatePicker } from "../../../components";
import { formatDurationLabel, roundedDate } from "../../../utils/datetime";
import { isValidPhoneNumber } from "../../../utils/phone";
import {
  meetURL,
  phoneNumber as phoneNumberRegex,
  teamsUrl,
  zoomURL,
} from "../../../utils/regex";
import { formatPhoneNumber } from "../../../utils/string";
import { Candidate, InterviewType, UserInfoFragment } from "../../graphql";
import useCurrentUser from "../../hooks/useCurrentUser";
import { PositionSelect, TimezoneSelect } from "..";
import { CallGuideSelectV2 } from "../CallGuideSelectV2/CallGuideSelectV2";
import CandidateSelect from "../CandidateSelect";
import InterviewerSelect from "./InterviewersSelect";

const durationMinutesOptions = [
  15, 30, 45, 60, 75, 90, 120, 150, 180, 210, 240, 270, 300, 330, 360, 390,
];

export interface FormValues {
  scheduledStartTime: string;
  timezoneName: string;
  durationMinutes: number;
  zoomJoinUrl?: string;
  googleMeetMeetingUrl?: string;
  teamsMeetingUrl?: string;
  phoneNumber?: string;
  candidateId?: string | null;
  candidateFirstName?: string;
  candidateLastName?: string;
  candidateEmail?: string;
  interviewerIds: string[];
  name?: string;
  positionId?: string;
  callGuideId?: string | null;
}

interface FormFields extends Omit<FormValues, "phoneNumber"> {
  meetingPhoneNumber?: string;
  candidatePhoneNumber?: string;
}

type MeetingInterviewType =
  | InterviewType.Phone
  | InterviewType.Zoom
  | InterviewType.GoogleMeet
  | InterviewType.Teams;

const meetingFieldByType: Record<MeetingInterviewType, keyof FormFields> = {
  [InterviewType.Phone]: "meetingPhoneNumber",
  [InterviewType.Zoom]: "zoomJoinUrl",
  [InterviewType.GoogleMeet]: "googleMeetMeetingUrl",
  [InterviewType.Teams]: "teamsMeetingUrl",
};

export interface ScheduledInterviewFormProps {
  mode?: "create" | "update" | "join";
  editRestriction?: boolean;
  defaultScheduledStartTime?: string;
  defaultTimezoneName?: string;
  defaultDurationMinutes?: number;
  defaultInterviewType?: InterviewType;
  defaultPhoneNumber?: string;
  defaultGoogleMeetMeetingUrl?: string;
  defaultTeamsMeetingUrl?: string;
  defaultZoomJoinUrl?: string;
  defaultCandidate?: Pick<Candidate, "id">;
  defaultInterviewers?: UserInfoFragment[];
  defaultName?: string;
  defaultCallGuideId?: string;
  defaultPositionId?: string;
  isLoading: boolean;
  submitButtonText?: string;
  onSubmit: (values: FormValues) => void;
}

export const ScheduledInterviewForm: React.FC<ScheduledInterviewFormProps> = ({
  mode = "create",
  editRestriction = false,
  submitButtonText = "Schedule",
  defaultScheduledStartTime = roundedDate().toISOString(),
  defaultTimezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone,
  defaultDurationMinutes = 30,
  defaultInterviewType = InterviewType.Phone,
  defaultPhoneNumber,
  defaultGoogleMeetMeetingUrl,
  defaultTeamsMeetingUrl,
  defaultZoomJoinUrl,
  defaultCandidate,
  defaultInterviewers = [],
  defaultCallGuideId,
  defaultPositionId,
  defaultName,
  isLoading,
  onSubmit,
}) => {
  const currentUser = useCurrentUser();
  const sectionBackground = "pageBackground";
  const formattedDefaultPhoneNumber = defaultPhoneNumber
    ? formatPhoneNumber(defaultPhoneNumber)
    : "";
  const {
    register,
    unregister,
    handleSubmit,
    trigger,
    formState: { errors },
    getValues,
    setValue,
    control,
  } = useForm<FormFields>({
    defaultValues: {
      scheduledStartTime: defaultScheduledStartTime,
      durationMinutes: defaultDurationMinutes,
      meetingPhoneNumber:
        defaultInterviewType === InterviewType.Phone
          ? formattedDefaultPhoneNumber
          : "",
      candidatePhoneNumber:
        defaultInterviewType === InterviewType.Phone
          ? ""
          : formattedDefaultPhoneNumber,
      googleMeetMeetingUrl: defaultGoogleMeetMeetingUrl,
      teamsMeetingUrl: defaultTeamsMeetingUrl,
      zoomJoinUrl: defaultZoomJoinUrl,
      candidateId: defaultCandidate?.id,
      interviewerIds: defaultInterviewers.map((i) => i.id),
      callGuideId: defaultCallGuideId,
      positionId: defaultPositionId,
      name: defaultName,
      timezoneName: defaultTimezoneName,
    },
  });
  const [interviewType, setInterviewType] = useState(defaultInterviewType);
  const [positionId, setPositionId] = useState(defaultPositionId);
  const [showCandidateSelect, setShowCandidateSelect] = useState(
    ["create", "join"].includes(mode)
  );
  const [showCandidateFields, setShowCandidateFields] = useState(false);
  const [showSubmit, setShowSubmit] = useState(
    ["update", "join"].includes(mode)
  );
  const [createCandidateValue, setCreateCandidateValue] = useState<string>();
  const initialInterviewTypeRef = useRef(defaultInterviewType);
  const initialMeetingValuesRef = useRef<Record<MeetingInterviewType, string>>({
    [InterviewType.Phone]:
      defaultInterviewType === InterviewType.Phone
        ? formattedDefaultPhoneNumber
        : "",
    [InterviewType.Zoom]: defaultZoomJoinUrl ?? "",
    [InterviewType.GoogleMeet]: defaultGoogleMeetMeetingUrl ?? "",
    [InterviewType.Teams]: defaultTeamsMeetingUrl ?? "",
  });

  const onSubmitForm = handleSubmit((formValues): void => {
    const {
      scheduledStartTime,
      meetingPhoneNumber,
      candidatePhoneNumber,
      ...rest
    } = formValues;
    const scheduledStart = new Date(scheduledStartTime);
    scheduledStart.setMinutes(
      scheduledStart.getMinutes() - scheduledStart.getTimezoneOffset()
    );
    const phoneNumber =
      (isPhoneInterview ? meetingPhoneNumber : candidatePhoneNumber) ||
      undefined;
    onSubmit({
      scheduledStartTime: scheduledStart.toISOString(),
      phoneNumber,
      ...rest,
    });
  });

  useEffect(() => {
    if (!createCandidateValue) return;
    if (createCandidateValue.includes("@")) {
      setValue("candidateEmail", createCandidateValue);
    } else if (/^[\d +\-()]+$/.test(createCandidateValue)) {
      setValue(
        isPhoneInterview ? "meetingPhoneNumber" : "candidatePhoneNumber",
        createCandidateValue
      );
    } else {
      const values = createCandidateValue.split(" ");
      setValue("candidateFirstName", values[0]);
      if (values.length > 1) {
        setValue("candidateLastName", values[1]);
      }
    }
  }, [createCandidateValue]);

  const showInterviewTypesSelect =
    mode !== "join" && currentUser.organization.interviewTypes.length > 1;
  const isPhoneInterview = interviewType === InterviewType.Phone;

  const intlOutboundEnabled = currentUser.isInternationalCallingEnabled;
  const phoneNumPlaceholder = intlOutboundEnabled
    ? "+xx xxxxx-xxxxx"
    : "(xxx) xxx-xxxx";

  const clearMeetingFieldValue = (meetingType: MeetingInterviewType): void => {
    setValue(meetingFieldByType[meetingType], "");
  };

  const shouldUnregisterMeetingField = (
    meetingType: MeetingInterviewType,
    selectedType: InterviewType
  ): boolean => {
    if (meetingType === selectedType) return false;
    return true;
  };

  const detectInterviewType = (value: string): MeetingInterviewType | null => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return null;
    if (zoomURL.test(trimmedValue)) return InterviewType.Zoom;
    if (meetURL.test(trimmedValue)) return InterviewType.GoogleMeet;
    if (teamsUrl.test(trimmedValue)) return InterviewType.Teams;
    const isPhone = intlOutboundEnabled
      ? isValidPhoneNumber(trimmedValue)
      : phoneNumberRegex.test(trimmedValue);
    return isPhone ? InterviewType.Phone : null;
  };

  const validatePhoneNumberValue = (value?: string): true | string => {
    if (!value) return true;
    if (intlOutboundEnabled) {
      return isValidPhoneNumber(value) || "Invalid phone number";
    }
    return phoneNumberRegex.test(value) || "Invalid phone number";
  };

  const triggerPhoneValidation = (
    fieldName: "meetingPhoneNumber" | "candidatePhoneNumber"
  ): void => {
    trigger(fieldName).catch(() => undefined);
  };

  useEffect(() => {
    if (isPhoneInterview) {
      if (getValues("meetingPhoneNumber")) {
        triggerPhoneValidation("meetingPhoneNumber");
      }
      return;
    }

    if (showCandidateFields && getValues("candidatePhoneNumber")) {
      triggerPhoneValidation("candidatePhoneNumber");
    }
  }, [getValues, isPhoneInterview, showCandidateFields, trigger]);

  const handleInterviewTypeChange = (
    selectedType: InterviewType,
    options?: { meetingValue?: string; clearField?: boolean }
  ): void => {
    const previousType = interviewType;

    if (showCandidateFields) {
      if (
        previousType === InterviewType.Phone &&
        selectedType !== InterviewType.Phone
      ) {
        const meetingPhoneNumber = getValues("meetingPhoneNumber");
        if (meetingPhoneNumber && !getValues("candidatePhoneNumber")) {
          setValue("candidatePhoneNumber", meetingPhoneNumber);
        }
      }

      if (
        previousType !== InterviewType.Phone &&
        selectedType === InterviewType.Phone
      ) {
        const candidatePhoneNumber = getValues("candidatePhoneNumber");
        if (candidatePhoneNumber && !getValues("meetingPhoneNumber")) {
          setValue("meetingPhoneNumber", candidatePhoneNumber);
        }
        unregister("candidatePhoneNumber", { keepValue: true });
      }
    }

    // workaround for react-hook-form issues when un-registering fields on unmount
    // manually unregister before setting a new interviewType
    (
      Object.entries(meetingFieldByType) as Array<
        [MeetingInterviewType, keyof FormFields]
      >
    ).forEach(([meetingType, fieldName]) => {
      if (shouldUnregisterMeetingField(meetingType, selectedType)) {
        unregister(fieldName);
      }
    });
    if (options?.clearField && previousType in meetingFieldByType) {
      clearMeetingFieldValue(previousType as MeetingInterviewType);
    }
    setInterviewType(selectedType);
    if (options?.meetingValue && selectedType in meetingFieldByType) {
      setValue(
        meetingFieldByType[selectedType as MeetingInterviewType],
        options.meetingValue
      );
    }
    if (
      mode === "update" &&
      !options?.meetingValue &&
      selectedType === initialInterviewTypeRef.current &&
      selectedType in meetingFieldByType
    ) {
      const originalValue =
        initialMeetingValuesRef.current[selectedType as MeetingInterviewType];
      setValue(
        meetingFieldByType[selectedType as MeetingInterviewType],
        originalValue
      );
    }
  };

  const handleMeetingFieldChange = (value: string): void => {
    const detectedType = detectInterviewType(value);
    if (!detectedType || detectedType === interviewType) return;
    if (!currentUser.organization.interviewTypes.includes(detectedType)) {
      return;
    }
    handleInterviewTypeChange(detectedType, {
      meetingValue: value,
      clearField: true,
    });
  };

  return (
    <form onSubmit={onSubmitForm}>
      <VStack spacing="8" align="stretch">
        <Box hidden={mode === "join"}>
          <VStack spacing="8" align="stretch">
            <Flex wrap="wrap" columnGap="4" rowGap="6">
              {editRestriction ? (
                ""
              ) : (
                <FormControl id="scheduledStartTime" flex="2">
                  <FormLabel>Interview Date</FormLabel>
                  <Controller
                    control={control}
                    name="scheduledStartTime"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <DatePicker
                        minDate={new Date()}
                        selected={new Date(value)}
                        required
                        showTimeSelect
                        timeIntervals={15}
                        dateFormat="MM/dd/yyyy   h:mm aa"
                        onChange={onChange}
                        onBlur={onBlur}
                        customInput={<CustomDateTimeInput minWidth="215px" />}
                        portalId="datepicker-portal"
                      />
                    )}
                  />
                </FormControl>
              )}
              {editRestriction ? (
                ""
              ) : (
                <FormControl id="durationMinutes" minW="fit-content" flex="1">
                  <FormLabel>Duration</FormLabel>
                  <Select
                    data-testid="interview-duration-select"
                    {...register("durationMinutes", { valueAsNumber: true })}
                    isRequired
                    placeholder="Duration"
                  >
                    {durationMinutesOptions.map((value) => (
                      <option key={value} value={value}>
                        {formatDurationLabel(value)}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Flex>
            {editRestriction ? (
              ""
            ) : (
              <FormControl id="timezoneName" isInvalid={!!errors.timezoneName}>
                <FormLabel>Timezone</FormLabel>
                <Controller
                  control={control}
                  name="timezoneName"
                  rules={{
                    required: "required",
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TimezoneSelect
                      timezone={value}
                      onChange={onChange}
                      data-testid="interview-timezone-select"
                    />
                  )}
                />
              </FormControl>
            )}
          </VStack>
        </Box>
        {!editRestriction && showCandidateSelect && (
          <FormControl
            id="candidateId"
            isRequired
            isInvalid={!!errors.candidateId}
          >
            <FormLabel>Candidate</FormLabel>
            <Controller
              name="candidateId"
              control={control}
              rules={{
                required: "Please select a candidate",
              }}
              render={({ field: { onChange, onBlur, ref } }) => (
                <CandidateSelect
                  autoFocus
                  ref={ref}
                  name="candidateId"
                  onSelect={(candidate) => {
                    onChange(candidate?.id);
                    if (isPhoneInterview && !getValues("meetingPhoneNumber")) {
                      setValue(
                        "meetingPhoneNumber",
                        candidate?.defaultPhoneNumber
                          ? formatPhoneNumber(candidate.defaultPhoneNumber)
                          : undefined
                      );
                    }
                  }}
                  onCreate={(value) => {
                    setCreateCandidateValue(value);
                    setShowCandidateFields(true);
                    setShowCandidateSelect(false);
                    setValue("candidateId", undefined);
                    unregister("candidateId");
                  }}
                  onBlur={onBlur}
                />
              )}
            />
            {errors.candidateId && (
              <FormErrorMessage>{errors.candidateId.message}</FormErrorMessage>
            )}
          </FormControl>
        )}
        {showCandidateFields && (
          <VStack
            align="stretch"
            spacing="6"
            p="5"
            backgroundColor={sectionBackground}
            borderRadius="md"
          >
            <HStack justify="space-between">
              <Heading size="xs" as="h2">
                Create a New Candidate
              </Heading>
              <Button
                variant="link"
                size="sm"
                onClick={() => {
                  setShowCandidateFields(false);
                  setShowCandidateSelect(true);
                  setValue("candidateFirstName", undefined);
                  setValue("candidateLastName", undefined);
                  setValue("candidateEmail", undefined);
                  setValue("candidatePhoneNumber", undefined);
                  setValue("meetingPhoneNumber", undefined);
                }}
              >
                Cancel
              </Button>
            </HStack>
            <HStack>
              <FormControl id="candidateFirstName" isRequired>
                <FormLabel>Candidate First Name</FormLabel>
                <Input
                  {...register("candidateFirstName")}
                  data-testid="candidateFirstName"
                />
                {errors.candidateFirstName && (
                  <FormErrorMessage>
                    {errors.candidateFirstName.message}
                  </FormErrorMessage>
                )}
              </FormControl>
              <FormControl id="candidateLastName" isRequired>
                <FormLabel>Candidate Last Name</FormLabel>
                <Input
                  {...register("candidateLastName")}
                  data-testid="candidateLastName"
                />
                {errors.candidateLastName && (
                  <FormErrorMessage>
                    {errors.candidateLastName.message}
                  </FormErrorMessage>
                )}
              </FormControl>
            </HStack>
            <HStack>
              <FormControl
                id="candidateEmail"
                isRequired={[
                  InterviewType.GoogleMeet,
                  InterviewType.Zoom,
                  InterviewType.Teams,
                ].includes(interviewType)}
              >
                <FormLabel>Candidate Email</FormLabel>
                <Input
                  type="email"
                  {...register("candidateEmail")}
                  data-testid="candidateEmail"
                />
                {errors.candidateEmail && (
                  <FormErrorMessage>
                    {errors.candidateEmail.message}
                  </FormErrorMessage>
                )}
              </FormControl>
              {/* candidate phone number is collected in a different field for phone interviews */}
              {!isPhoneInterview && (
                <FormControl
                  id="candidatePhoneNumber"
                  isInvalid={!!errors.candidatePhoneNumber}
                >
                  <FormLabel>Candidate Phone</FormLabel>
                  <Input
                    type="tel"
                    placeholder={phoneNumPlaceholder}
                    {...register("candidatePhoneNumber", {
                      validate: {
                        isValid: validatePhoneNumberValue,
                      },
                      onChange: () =>
                        triggerPhoneValidation("candidatePhoneNumber"),
                    })}
                    data-testid="candidatePhoneNumber"
                  />
                  {errors.candidatePhoneNumber && (
                    <FormErrorMessage>
                      {errors.candidatePhoneNumber.message}
                    </FormErrorMessage>
                  )}
                </FormControl>
              )}
            </HStack>
          </VStack>
        )}
        <Flex columnGap="4">
          {!editRestriction && showInterviewTypesSelect && (
            <FormControl id="type" isRequired flex="0 0 130px">
              <FormLabel>Type</FormLabel>
              <Select
                data-testid="type-select"
                name="type"
                textTransform="capitalize"
                value={interviewType}
                onChange={(e) => {
                  const selectedType = e.target.value as InterviewType;
                  handleInterviewTypeChange(selectedType, { clearField: true });
                }}
              >
                {currentUser.organization.interviewTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.replace("_", " ").toLowerCase()}
                  </option>
                ))}
              </Select>
            </FormControl>
          )}
          {editRestriction ? (
            ""
          ) : isPhoneInterview ? (
            <FormControl
              key={interviewType}
              id="meetingPhoneNumber"
              isRequired
              isInvalid={!!errors.meetingPhoneNumber}
            >
              <FormLabel>Phone Number</FormLabel>
              <Input
                autoComplete="on"
                type="tel"
                data-testid="scheduled-interview-phoneNumber"
                {...register("meetingPhoneNumber", {
                  validate: {
                    isValid: validatePhoneNumberValue,
                  },
                  required: "Phone number is required",
                  onChange: (event) => {
                    handleMeetingFieldChange(event.target.value);
                    triggerPhoneValidation("meetingPhoneNumber");
                  },
                })}
                placeholder={phoneNumPlaceholder}
              />
              {errors.meetingPhoneNumber && (
                <FormErrorMessage>
                  {errors.meetingPhoneNumber.message}
                </FormErrorMessage>
              )}
            </FormControl>
          ) : interviewType === InterviewType.Zoom ? (
            <FormControl
              key={interviewType}
              id="zoomJoinUrl"
              isRequired
              isInvalid={!!errors.zoomJoinUrl}
            >
              <FormLabel>Zoom Meeting URL</FormLabel>
              <Input
                {...register("zoomJoinUrl", {
                  pattern: {
                    value: zoomURL,
                    message: "Please enter a valid Zoom URL",
                  },
                  required: "Zoom URL is required",
                  onChange: (event) =>
                    handleMeetingFieldChange(event.target.value),
                })}
                data-testid="zoomJoinUrl"
                placeholder="https://zoom.us/j/1234567890?pwd=dm5qwerty"
              />
              {errors.zoomJoinUrl && (
                <FormErrorMessage>
                  {errors.zoomJoinUrl.message}
                </FormErrorMessage>
              )}
            </FormControl>
          ) : interviewType === InterviewType.GoogleMeet ? (
            <FormControl
              key={interviewType}
              id="googleMeetMeetingUrl"
              isRequired
              isInvalid={!!errors.googleMeetMeetingUrl}
            >
              <FormLabel>Meet URL</FormLabel>
              <Input
                {...register("googleMeetMeetingUrl", {
                  pattern: {
                    value: meetURL,
                    message: "Please enter a valid Google Meet URL",
                  },
                  required: "Meet URL is required",
                  onChange: (event) =>
                    handleMeetingFieldChange(event.target.value),
                })}
                data-testid="googleMeetMeetingUrl"
                placeholder="https://meet.google.com/abc-defg-hij"
              />
              {errors.googleMeetMeetingUrl && (
                <FormErrorMessage>
                  {errors.googleMeetMeetingUrl?.message}
                </FormErrorMessage>
              )}
            </FormControl>
          ) : interviewType === InterviewType.Teams ? (
            <FormControl
              key={interviewType}
              id="teamsMeetingUrl"
              isRequired
              isInvalid={!!errors.teamsMeetingUrl}
            >
              <FormLabel>Microsoft Teams URL</FormLabel>
              <Input
                {...register("teamsMeetingUrl", {
                  pattern: {
                    value: teamsUrl,
                    message: "Please enter a valid Microsoft Teams URL",
                  },
                  required: "Microsoft Teams URL is required",
                  onChange: (event) =>
                    handleMeetingFieldChange(event.target.value),
                })}
                data-testid="teamsMeetingUrl"
                placeholder="https://teams.microsoft.com/l/meetup-join/19:d00b61fe60f04a6697606d90dacac90b@thread.tacv2/1669923176555?context=%7B%22Tid%22:%228292a654-7ff5-4cf6-8ab7-1537784c3eaf%22,%22Oid%22:%2208c7628a-42bb-4822-8c89-ab9fa6705eda%22%7D"
              />
              {errors.teamsMeetingUrl && (
                <FormErrorMessage>
                  {errors.teamsMeetingUrl?.message}
                </FormErrorMessage>
              )}
            </FormControl>
          ) : null}
        </Flex>
        {editRestriction ? (
          ""
        ) : (
          <FormControl
            id="interviewerIds"
            isRequired
            isInvalid={!!errors.interviewerIds}
          >
            <FormLabel>Interviewers</FormLabel>
            <Controller
              name="interviewerIds"
              control={control}
              rules={{
                validate: (value) =>
                  (value && value.length > 0) ||
                  "Please select at least one interviewer",
              }}
              render={({ field: { onChange, onBlur, ref } }) => (
                <InterviewerSelect
                  ref={ref}
                  defaultInterviewers={defaultInterviewers}
                  onChange={(interviewers) => {
                    onChange(interviewers.map((i) => i.id));
                    if (!showSubmit) setShowSubmit(true);
                  }}
                  onBlur={onBlur}
                />
              )}
            />
            {errors.interviewerIds && (
              <FormErrorMessage>
                {
                  (errors.interviewerIds as unknown as { message: string })
                    .message
                }
              </FormErrorMessage>
            )}
          </FormControl>
        )}
        {showSubmit && (
          <>
            <VStack
              backgroundColor={sectionBackground}
              p="5"
              spacing={8}
              borderRadius="md"
            >
              <FormControl id="callGuideId">
                <FormLabel>Interview Guide (optional)</FormLabel>
                <Controller
                  name="callGuideId"
                  control={control}
                  render={({ field }) => (
                    <CallGuideSelectV2
                      {...field}
                      placeholder="Type to filter guides"
                      positionId={positionId}
                      defaultCallGuideId={defaultCallGuideId}
                      autoFocus={mode === "create"}
                      onSelect={(callGuide) => {
                        const values = getValues(["positionId", "name"]);
                        setValue("callGuideId", callGuide?.id ?? "");
                        if (
                          callGuide?.position?.id &&
                          !values[0] &&
                          callGuide.id !== defaultCallGuideId
                        ) {
                          setValue("positionId", callGuide.position.id);
                          setPositionId(callGuide.position.id);
                        }
                        if (callGuide?.name && !values[1]) {
                          setValue("name", callGuide?.name);
                        }
                      }}
                    />
                  )}
                />
                {errors.callGuideId && (
                  <FormErrorMessage>
                    {errors.callGuideId.message}
                  </FormErrorMessage>
                )}
              </FormControl>
              <FormControl id="positionId">
                <FormLabel>Position (optional)</FormLabel>
                <Controller
                  name="positionId"
                  control={control}
                  render={({ field }) => (
                    <PositionSelect
                      {...field}
                      positionId={positionId}
                      onSelect={(position) => {
                        setValue("positionId", position?.id ?? "");
                        setPositionId(position?.id ?? "");
                      }}
                    />
                  )}
                />
                {errors.positionId && (
                  <FormErrorMessage>
                    {errors.positionId.message}
                  </FormErrorMessage>
                )}
              </FormControl>
              {editRestriction ? (
                ""
              ) : (
                <FormControl id="name">
                  <FormLabel>Interview Name (optional)</FormLabel>
                  <Input data-testid="interview-name" {...register("name")} />
                  {errors.name && (
                    <FormErrorMessage>{errors.name.message}</FormErrorMessage>
                  )}
                </FormControl>
              )}
            </VStack>
            <Button
              data-testid="scheduled-interview-submit-button"
              type="submit"
              mt={8}
              isLoading={isLoading}
              color="white"
            >
              {submitButtonText}
            </Button>
          </>
        )}
      </VStack>
    </form>
  );
};

export default ScheduledInterviewForm;
